import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { gapi } from "gapi-script";
import apis from "../utils/apis";
import { handleApiError } from "../store/actions/utils/utils";
import { useAppDispatch } from "../store";
import type { ExternalPickedItem } from "../components/shared/files/filesPickerTypes";
import { INTEGRATIONS_SETTINGS_PATH } from "../components/shared/files/filesPickerTypes";
import { IntegrationNotConnectedError } from "../components/shared/files/externalImport";

export interface GooglePickOptions {
  /** Extensions without dots; narrows the picker's MIME filter. */
  accept?: string[];
  /** Default true. */
  multiple?: boolean;
}

const EXTENSION_MIME: Record<string, string[]> = {
  pdf: ["application/pdf"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.google-apps.document"],
  txt: ["text/plain"],
  csv: ["text/csv"],
  xls: ["application/vnd.ms-excel"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.google-apps.spreadsheet"],
  ppt: ["application/vnd.ms-powerpoint"],
  pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation", "application/vnd.google-apps.presentation"],
  msg: ["application/vnd.ms-outlook"],
};

/** The picker's MIME filter for an accept list; undefined = no filter. */
const mimeTypesFor = (accept?: string[]): string | undefined => {
  if (!accept || !accept.length) return undefined;
  const mimes = accept.flatMap((ext) => EXTENSION_MIME[ext.replace(/^\./, "").toLowerCase()] || []);
  return mimes.length ? Array.from(new Set(mimes)).join(",") : undefined;
};

interface GoogleDriveContextProps {
  accessToken: string | null;
  isSignedIn: boolean;
  initClient: () => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => void;
  listFiles: () => Promise<any>;
  downloadFile: (fileId: string, fileName: string) => Promise<void>;
  handleFileSelection: (fileId: string) => Promise<void>;
  initTokenClient: () => void;
  /** Opens the Google Picker; resolves with Drive item ids (null on cancel). */
  pickItems: (options?: GooglePickOptions) => Promise<ExternalPickedItem[] | null>;
  /** @deprecated Use `pickItems`. */
  handleOpenPicker: (onPicked: (items: ExternalPickedItem[]) => void) => void;
  pickerLoaded: boolean;
  getBackendToken: () => Promise<void>;   
}

const defaultContext: GoogleDriveContextProps = {
  accessToken: null,
  isSignedIn: false,
  initClient: async () => { },
  signIn: async () => { },
  signOut: () => { },
  listFiles: async () => [],
  downloadFile: async () => { },
  handleFileSelection: async () => { },
  initTokenClient: async () => { },
  pickItems: async () => null,
  handleOpenPicker: async () => { },
  pickerLoaded: false,
  getBackendToken: async () => { },
};

export let tokenClient: any;
export let accessToken: string | null = null;

const GoogleDriveContext = createContext<GoogleDriveContextProps>(defaultContext);

export const GoogleDriveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const API_KEY =
    process.env.REACT_APP_GOOGLE_API_KEY || "";
  // The Cloud project NUMBER. With the `drive.file` scope a picked file is
  // shared with this project, which is what lets the backend download it.
  const APP_ID = process.env.REACT_APP_GOOGLE_APP_ID || "";

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const dispatch = useAppDispatch()

  const initClient = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      gapi.load("client", async () => {
        try {
          await gapi.client.init({
            apiKey: API_KEY,
            discoveryDocs: [
              "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
            ],
          });
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  }, [API_KEY]);


  const signIn = async () => {
    return new Promise<void>((resolve, reject) => {
      if (accessToken) {
        resolve();
      } else {
        tokenClient.callback = (tokenResponse: any) => {
          if (tokenResponse.error) {
            reject(tokenResponse.error);
            return;
          }
          accessToken = tokenResponse.access_token;
          gapi.client.setToken({ access_token: accessToken });
          resolve();
        };
        tokenClient.requestAccessToken({ prompt: 'consent' });
      }
    });
  };

  const getBackendToken = async () => {
    try {
      if (accessToken) return

      const response = await apis.get<{ access_token: string }>(
        "/integrations/auth/tokens/gdrive",
        { withCredentials: true }
      )

      if (response.status === 200 && response.data.access_token) {
        accessToken = response.data.access_token
        gapi.client.setToken({ access_token: accessToken })
      } else {
        handleApiError(response, dispatch)
      }
    } catch (err: any) {
    }

  }

  // Sign Out
  const signOut = () => {
    if (accessToken) {
      window.google.accounts.oauth2.revoke(accessToken, () => {
        setIsSignedIn(false);
        gapi.client.setToken(null);
      });
    }
  };

  const listFiles = async () => {
    return gapi.client.drive.files.list();
  };

  const downloadFile = async (fileId: string, fileName: string) => {
    try {
      if (!accessToken) await signIn();

      const response = await gapi.client.request({
        path: `/drive/v3/files/${fileId}`,
        method: "GET",
        params: { alt: "media" },
        responseType: "blob",
      });

      const blob = new Blob([response.body], {
        type: response.headers["Content-Type"],
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Silently handle download errors
    }
  };

  const handleFileSelection = async (fileId: string) => {
    try {
      await gapi.client.drive.files.get({
        fileId,
        alt: "media",
      });
    } catch (error) {
      // Silently handle file selection errors
    }
  };

  /**
   * Open the Google Picker and resolve with the picked Drive ITEM ids (F-DMS).
   *
   * Nothing is downloaded in the browser: the caller hands the ids to
   * `POST /integrations/gdrive/import`, which fetches the bytes server-side
   * with the stored integration token. Resolves `null` when the user cancels;
   * rejects with `IntegrationNotConnectedError` when Drive is not connected.
   */
  const pickItems = async (options?: GooglePickOptions): Promise<ExternalPickedItem[] | null> => {
    // The token can expire; ask the backend for a fresh one on every pick.
    accessToken = null;
    await getBackendToken();
    if (!accessToken) throw new IntegrationNotConnectedError('gdrive');
    const token = accessToken;

    return new Promise<ExternalPickedItem[] | null>((resolve, reject) => {
      try {
        gapi.load("picker", () => {
          try {
            const docsView = new google.picker.DocsView()
              .setIncludeFolders(true)
              .setSelectFolderEnabled(false)
              .setMode(google.picker.DocsViewMode.LIST);
            const mimes = mimeTypesFor(options?.accept);
            if (mimes) docsView.setMimeTypes(mimes);

            let builder = new google.picker.PickerBuilder()
              .addView(docsView)
              .setOAuthToken(token)
              .setDeveloperKey(API_KEY)
              .setAppId(APP_ID)
              .setCallback((data: any) => {
                const action = data?.action;
                if (action === google.picker.Action.PICKED) {
                  const docs = data[google.picker.Response.DOCUMENTS] || data.docs || [];
                  resolve(
                    docs.map((doc: any) => ({
                      id: doc[google.picker.Document.ID] || doc.id,
                      name: doc[google.picker.Document.NAME] || doc.name,
                    }))
                  );
                } else if (action === google.picker.Action.CANCEL) {
                  resolve(null);
                }
              });
            if (options?.multiple !== false) {
              builder = builder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
            }
            builder.build().setVisible(true);
          } catch (err) {
            reject(err);
          }
        });
      } catch (err) {
        reject(err);
      }
    });
  };

  /** @deprecated Use `pickItems`. Calls back with the picked Drive item ids (not files). */
  const handleOpenPicker = async (onPicked: (items: ExternalPickedItem[]) => void) => {
    try {
      const items = await pickItems();
      if (items && items.length) onPicked(items);
    } catch (err) {
      if (err instanceof IntegrationNotConnectedError) {
        window.location.href = INTEGRATIONS_SETTINGS_PATH;
      }
    }
  };

  useEffect(() => {
    (async () => {
      initClient()
        .then(() => {
          setPickerLoaded(true);
        })
        .catch((error) => {
          // Silently handle initialization errors
        });
    })();
  }, [initClient]);

  return (
    <GoogleDriveContext.Provider
      value={{
        accessToken,
        isSignedIn,
        initClient,
        signIn,
        signOut,
        listFiles,
        downloadFile,
        handleFileSelection,
        initTokenClient: () => { },
        pickItems,
        handleOpenPicker,
        pickerLoaded,
        getBackendToken,
      }}
    >
      {children}
    </GoogleDriveContext.Provider>
  );
};

export const useGoogleDrive = () => {
  return useContext(GoogleDriveContext);
};
